<template>
    <div class="w-100 p-1 px-3 my-1" :class="{ widget: enteringData }">
        <div class="w-100 d-flex justify-content-end">
            <p @click="enterData()" v-if="!enteringData" class="text-primary cursor-pointer mb-0">
                <em>Phase Change Lines</em><i class="ml-2 fa fa-times-circle-o rot-45"></i>
            </p>
        </div>
        <div class="w-100 pb-3" v-show="enteringData">
            <div class="d-flex justify-content-between align-items-center w-100">
                <p class="text-uppercase font-weight-bold py-1 mb-0">Phase Change Lines</p>
                <i
                    v-if="!enteringData"
                    @click="enteringData = true"
                    class="fa fa-edit cursor-pointer mx-1 text-primary ml-auto"
                ></i>
                <i
                    @click="closeInput"
                    class="fa fa-times-circle-o cursor-pointer mx-1 text-primary"
                ></i>
            </div>
            <hr
                style="width:100%;margin:auto;color:#c3c3c3;background: #c3c3c3; height: 1px; margin-bottom: 0.5rem;"
            />
            <div class="row mx-0 px-3" style="background-color: #323336">
                <div v-for="(title, i) in headers.titles" :class="`col-${headers.col[i]} px-1`">
                    <span class="small text-uppercase text-white">{{ title }}</span>
                </div>
            </div>

            <div class="row px-3 my-2">
                <div class="col-2">
                    <v-date-picker
                        dusk="date"
                        placeholder="MM/DD/YYYY"
                        id="date"
                        class="picker-container"
                        popover-align="left"
                        v-model="phaseChangeLine.phase_change_at"
                    ></v-date-picker>
                </div>
                <div class="col-2">
                    <input
                        v-mask="'XXX'"
                        type="text"
                        v-model="phaseChangeLine.character"
                        placeholder="XYZ"
                        class="form-control-sm form-control"
                        style="max-width: 4rem;"
                    />
                </div>
                <div class="col-6">
                    <input
                        type="text"
                        class="form-control form-control-sm"
                        v-model="phaseChangeLine.notes"
                    />
                </div>
                <div class="col-2">
                    <span
                        :class="hasNotUniqueError ? 'cursor-not-allowed' : 'cursor-pointer'"
                        @click="hasNotUniqueError ? null : addEntry()"
                        dusk="add-data"
                    >
                        <i v-if="loading" class="fa fa-spin fa-circle-o-notch text-primary"></i>
                        <i v-else="" class="fa fa-times-circle-o rot-45 text-primary"></i>
                        <em style="white-space: nowrap;" class="small text-primary"
                            >Add Data</em
                        ></span
                    >
                </div>
            </div>

            <div class="row px-3 my-1" v-for="(line, index) in phaseChangeLines">
                <template v-if="editLineIndex === index">
                    <div class="col-2 px-1">
                        <v-date-picker
                            dusk="date"
                            placeholder="MM/DD/YYYY"
                            id="date"
                            class="picker-container"
                            popover-align="left"
                            v-model="line.phase_change_at"
                        ></v-date-picker>
                    </div>
                    <div class="col-2 px-1">
                        <input
                            v-mask="'XXX'"
                            type="text"
                            v-model="line.character"
                            placeholder="XYZ"
                            class="form-control-sm form-control"
                            style="max-width: 4rem;"
                        />
                    </div>
                    <div class="col-6 px-1">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            v-model="line.notes"
                        />
                    </div>
                    <div class="col-2 d-flex">
                        <div
                            @click="cancelEdit()"
                            class="d-flex mr-2 justify-content-center align-items-center ml-2 cursor-pointer"
                        >
                            <i class="fa fa-times-circle-o text-muted mr-1"></i>
                            <small><em class="text-muted text-nowrap">Cancel</em></small>
                        </div>
                        <div
                            :class="hasNotUniqueError ? 'cursor-not-allowed' : 'cursor-pointer'"
                            @click="hasNotUniqueError ? null : savePhaseChangeLines()"
                            class="d-flex  justify-content-center align-items-center ml-2"
                        >
                            <i
                                :class="loading ? 'fa-spin fa-circle-o-notch' : 'fa-save'"
                                class="fa text-primary mr-1"
                            ></i>
                            <small><em class="text-primary text-nowrap">Save</em></small>
                        </div>
                    </div>
                </template>
                <template v-else="">
                    <div class="col-2">
                        {{
                            line.phase_change_at
                                ? moment(line.phase_change_at).format("MM/DD/YY")
                                : ""
                        }}
                    </div>
                    <div class="col-2">
                        {{ line.character }}
                    </div>
                    <div class="col-6">
                        {{ line.notes }}
                    </div>
                    <div class="col-2 d-flex">
                        <div
                            @click="deletePhaseChangeLine(index)"
                            class="d-flex mr-2 justify-content-center align-items-center ml-2 cursor-pointer"
                        >
                            <i class="fa fa-trash-o text-danger mr-1"></i>
                            <small><em class="text-primary text-danger">Delete</em></small>
                        </div>
                        <div
                            @click="editPhaseChangeLine(line, index)"
                            class="d-flex justify-content-center align-items-center ml-2 cursor-pointer"
                        >
                            <i class="fa fa-edit text-primary mr-1"></i>
                            <small><em class="text-primary text-nowrap">Edit</em></small>
                        </div>
                    </div>
                </template>
            </div>

            <div class="alert alert-warning mt-3 mx-3" v-if="hasNotUniqueError || validationError">
                <span class="font-italic"
                    ><i class="fa fa-exclamation-triangle text-warning mr-2"></i>Characters and
                    dates need to be filled and can't be repeated.</span
                ><span>&nbsp;</span>
            </div>
        </div>
    </div>
</template>

<script>
import { fixDatePickers } from "../../plugins/fixDatePickers";
import ProfilePicture from "../ui/ProfilePicture";

export default {
    name: "GoalsPhaseChange",
    props: ["goal", "userId"],
    mixins: [fixDatePickers],
    data() {
        return {
            enteringData: false,
            phaseChangeLines: [],
            loading: false,
            saving: false,
            chartMetaData: null,
            headers: {
                titles: [`Date`, `Character`, `Note`, ``],
                col: [2, 2, 6, 2],
            },
            phaseChangeLine: this.createPhaseChangeLine(),
            editLineIndex: null,
            validationError: false,
        };
    },
    created() {
        this.$bus.$on("chart-metadata", payload => {
            this.chartMetaData = payload;
        });
    },
    watch: {
        goal() {
            this.populateLocalPhaseChangeLines();
        },
    },
    computed: {
        hasNotUniqueError() {
            let characters = this.phaseChangeLines.map(line => line.character);
            let unique = Array.from(new Set(characters));
            return (
                this.phaseChangeLines
                    .map(line => line.character)
                    .includes(this.phaseChangeLine.character) ||
                this.phaseChangeLines.map(line => line.character).length > unique.length
            );
        },
    },
    methods: {
        createPhaseChangeLine() {
            return {
                phase_change_at: new Date(),
                created_by_user_id: this.userId,
                character: "",
                notes: "",
            };
        },

        cancelEdit() {
            this.editLineIndex = null;
            this.phaseChangeLine = this.createPhaseChangeLine();
        },

        addEntry() {
            let dates = [];
            if (this.goal_phase_change_at) {
                dates = this.goal.phase_change_lines.map(line => line.phase_change_at);
            }
            let characters = this.phaseChangeLines.map(line => line.character);

            if (
                !this.phaseChangeLine.phase_change_at ||
                !this.phaseChangeLine.character ||
                dates.includes(
                    this.moment(this.phaseChangeLine.phase_change_at).format("YYYY-MM-DD")
                ) ||
                characters.includes(this.phaseChangeLine.character)
            ) {
                this.validationError = true;
                return;
            }

            this.phaseChangeLines.push(this.phaseChangeLine);
            this.savePhaseChangeLines();
        },

        prepareSavePayload() {
            let data = [];
            this.phaseChangeLines.forEach(line => {
                line.phase_change_at = this.moment(line.phase_change_at).format("YYYY-MM-DD");
                data.push({ ...line });
            });

            let dates = this.phaseChangeLines.map(line => line.phase_change_at);
            let unique = Array.from(new Set(dates));

            if (dates.length > unique.length) {
                return false;
            }

            return data;
        },

        deletePhaseChangeLine(index) {
            this.phaseChangeLines.splice(index, 1);
            this.savePhaseChangeLines();
        },

        editPhaseChangeLine(line, index) {
            this.editLineIndex = index;
            setTimeout(this.removePickerMinWidth, 100);
        },

        savePhaseChangeLines() {
            this.validationError = false;
            this.loading = true;
            this.editLineIndex = null;

            if (!this.prepareSavePayload()) {
                this.validationError = true;
                this.populateLocalPhaseChangeLines();
                return;
            }

            const data = {
                phase_change_lines: this.prepareSavePayload(),
            };

            axios
                .patch(`/api/patients/${this.goal.patient.id}/goals/${this.goal.id}`, data)
                .then(r => {
                    this.loading = false;
                    this.$emit("goal-updated");

                    this.phaseChangeLine = this.createPhaseChangeLine();
                    this.populateLocalPhaseChangeLines();

                    this.$bus.$emit("redraw-chart");
                })
                .catch(error => {
                    error = console.error(error);
                });
        },

        enterData() {
            this.populateLocalPhaseChangeLines();
            this.enteringData = true;
        },

        populateLocalPhaseChangeLines() {
            this.phaseChangeLines = [];
            if (this.goal.phase_change_lines) {
                let goalPhaseChangeLines = JSON.parse(JSON.stringify(this.goal.phase_change_lines));
                goalPhaseChangeLines.forEach(line => {
                    line.phase_change_at = this.moment(line.phase_change_at).toDate();
                    this.phaseChangeLines.push({ ...line });
                });
                this.phaseChangeLines.sort((a, b) => {
                    if (
                        this.moment(a.phase_change_at).format("X") <
                        this.moment(b.phase_change_at).format("X")
                    ) {
                        return -1;
                    }
                    return 1;
                });
            }
        },

        closeInput() {
            this.enteringData = false;
            this.phaseChangeLines = [];
        },
    },
};
</script>

<style scoped lang="scss">
.widget {
    background-color: #fff;
}
</style>
